import usePageTitle from '../hooks/usePageTitle';
import React, { FormEvent, useEffect, useState } from 'react';
import { Mail, Shield, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { usersService } from '../services';
import NotificationPrefsCard from '../components/NotificationPrefsCard';

const Profile: React.FC = () => {
  usePageTitle('Profile');
  const { currentUser, setCurrentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(currentUser?.name ?? '');
  }, [currentUser?.id, currentUser?.name]);

  if (!currentUser) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Profile</h1>
        </div>
        <div className="page-content">
          <p>Sign in to manage your profile.</p>
        </div>
      </div>
    );
  }

  const trimmedName = name.trim();
  const dirty = trimmedName !== (currentUser.name ?? '');
  const canSubmit = trimmedName.length > 0 && dirty && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const updated = await usersService.updateUser(currentUser.id, {
        name: trimmedName,
      });
      setCurrentUser(updated);
      showSuccess('Profile updated');
    } catch (err) {
      showError(
        'Could not update profile',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Profile</h1>
        <p>Manage your account information.</p>
      </div>

      <div className="page-content" style={{ display: 'grid', gap: '1.5rem' }}>
        <form
          className="card"
          onSubmit={handleSubmit}
          style={{ padding: '1.5rem', maxWidth: 640, display: 'grid', gap: '1rem' }}
        >
          <h2 style={{ marginTop: 0 }}>
            <UserIcon size={20} style={{ verticalAlign: '-0.25rem', marginRight: '0.5rem' }} />
            Personal information
          </h2>

          <label>
            <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>Display name</div>
            <input
              className="form-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              required
              style={{ width: '100%' }}
            />
          </label>

          <label>
            <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>
              <Mail size={14} style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />
              Email
            </div>
            <input
              className="form-input"
              type="email"
              value={currentUser.email}
              readOnly
              style={{
                width: '100%',
                background: 'var(--background, #f6f8fb)',
                color: 'var(--text-secondary)',
              }}
            />
            <div
              style={{
                marginTop: '0.25rem',
                fontSize: '0.8125rem',
                color: 'var(--text-secondary)',
              }}
            >
              Email is managed by your sign-in provider and can&rsquo;t be changed here.
            </div>
          </label>

          <div
            style={{
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              borderTop: '1px solid var(--border, #e3e8ef)',
              paddingTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Shield size={14} />
            Role: <strong style={{ color: 'var(--text-primary)' }}>{currentUser.role.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}</strong>
            {(currentUser.role === 'client' || currentUser.role === 'facility_manager') && <span>· Contact your consultant to change role or organization.</span>}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit}
            style={{ justifySelf: 'start' }}
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </form>
        <NotificationPrefsCard user={currentUser} onSaved={setCurrentUser} />
      </div>
    </div>
  );
};

export default Profile;
